import React, { useState } from "react";
import { Hooks } from "@xt/xtbase";
import styles from "../ConvertBuy/index.module.less";
import FiatCurrencyIcon from "../FiatCurrencyIcon";
import ConvertInputCardsStack from "../ConvertInputCardsStack";
import ConvertCurrencyDrawer from "../ConvertCurrencyDrawer";

const { useTranslation } = Hooks;

/** 仅允许数字与一个小数点，与原先数字键盘输入规则一致 */
function sanitizeDecimalInput(raw) {
  if (raw == null || raw === "") return "";
  let value = String(raw).replace(/[^\d.]/g, "");
  const dotIndex = value.indexOf(".");
  if (dotIndex !== -1) {
    value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, "");
  }
  return value;
}

function CurrencyTrigger({ selectedCode, options, onOpen, ariaLabel }) {
  const selected = options.find((o) => o.code === selectedCode) || { code: selectedCode, color: "#26A69A" };

  return (
    <button
      type="button"
      className={styles["convert-buy__currency-wrap"]}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
    >
      <div className={styles["convert-buy__currency"]}>
        <FiatCurrencyIcon
          code={selected.code}
          className={styles["convert-buy__currency-icon"]}
          style={{ background: selected.color || "#26A69A" }}
        />
        <span className={styles["convert-buy__currency-code"]}>{selected.code}</span>
        <span className={styles["convert-buy__chevron"]}>
          <svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none">
            <path d="M6 0V1.5L3 4.68L0 1.5V0H6Z" fill="white" />
          </svg>
        </span>
      </div>
    </button>
  );
}

export default function ConvertForm(props) {
  const t = useTranslation();
  const {
    spendAmount,
    setSpendAmount,
    spendCurrency,
    receiveCurrency,
    spendRange,
    receiveRange,
    receiveAmount = "",
    spendAvailable = "",
    spendMin = null,
    spendMax = null,
    fromOptions = [],
    toOptions = [],
    fromBalanceMap = null,
    showFromBalances = false,
    onFromDrawerOpen,
    fromCurrency,
    toCurrency,
    onFromChange,
    onToChange,
    onSwapDirection,
    onInputActivate,
    onInputDeactivate,
    previewErrorKey = null,
  } = props;

  const [currencyDrawerOpen, setCurrencyDrawerOpen] = useState(false);
  const [currencyDrawerSide, setCurrencyDrawerSide] = useState("from");

  const availableNum = parseFloat(String(spendAvailable).replace(/,/g, "")) || 0;
  const amountNum = spendAmount ? parseFloat(String(spendAmount).replace(/,/g, "")) : NaN;
  const hasAmount = !Number.isNaN(amountNum) && amountNum > 0;

  let errorKey = null;
  if (hasAmount) {
    if (amountNum > availableNum) errorKey = "convert.errorInsufficient";
    else if (spendMin != null && amountNum < spendMin) errorKey = "convert.errorMin";
    else if (spendMax != null && amountNum > spendMax) errorKey = "convert.errorMax";
    else if (previewErrorKey && previewErrorKey !== "convert.errorAmountTooSmallAfterFees") {
      errorKey = previewErrorKey;
    }
  }

  const formatAvailable = (v) => {
    const n = parseFloat(String(v).replace(/,/g, ""));
    if (Number.isNaN(n)) return "0";
    if (Number.isInteger(n)) return String(n);
    const s = n.toFixed(8).replace(/\.?0+$/, "");
    return s || "0";
  };

  const handleMax = () => {
    const cap = spendMax != null ? spendMax : availableNum;
    const use = Math.min(availableNum, cap);
    setSpendAmount(use > 0 ? String(use) : "0");
  };

  const handleAmountChange = (e) => {
    setSpendAmount(sanitizeDecimalInput(e.target.value));
  };

  const openCurrencyDrawer = (side) => {
    setCurrencyDrawerSide(side);
    setCurrencyDrawerOpen(true);
    if (side === "from") onFromDrawerOpen?.();
  };

  return (
    <>
      <ConvertInputCardsStack
        onSwapDirection={onSwapDirection}
        fromCard={
          <div className={styles["convert-buy__card"]}>
            <div className={styles["convert-buy__card-head"]}>
              <span className={styles["convert-buy__card-label"]}>{t("convert.from")}</span>
              <span className={styles["convert-buy__available"]}>
                {t("convert.available")} {spendAvailable !== "" ? formatAvailable(spendAvailable) : ""} {spendCurrency}
              </span>
            </div>
            <div className={styles["convert-buy__card-row"]}>
              <CurrencyTrigger
                selectedCode={fromCurrency}
                options={fromOptions}
                onOpen={() => openCurrencyDrawer("from")}
                ariaLabel={t("convert.from")}
              />
              <div className={styles["convert-buy__input-row"]}>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className={`${styles["convert-buy__input"]} ${spendAmount ? styles["convert-buy__input--value"] : ""}`}
                  value={spendAmount}
                  placeholder={spendRange}
                  aria-label={t("convert.from")}
                  onChange={handleAmountChange}
                  onFocus={onInputActivate}
                  onBlur={onInputDeactivate}
                />
              </div>
            </div>
            <div className={styles["convert-buy__card-footer"]}>
              {errorKey && (
                <span className={styles["convert-buy__error"]}>
                  {t(errorKey, { min: spendMin, max: spendMax })}
                </span>
              )}
              <span className={styles["convert-buy__max-btn"]} onClick={handleMax}>
                {t("convert.max")}
              </span>
            </div>
          </div>
        }
        toCard={
          <div className={styles["convert-buy__card"]}>
            <div className={styles["convert-buy__card-label"]}>{t("convert.to")}</div>
            <div className={styles["convert-buy__card-row"]}>
              <CurrencyTrigger
                selectedCode={toCurrency}
                options={toOptions}
                onOpen={() => openCurrencyDrawer("to")}
                ariaLabel={t("convert.to")}
              />
              <div className={styles["convert-buy__receive-right"]}>
                {receiveAmount ? (
                  <span className={styles["convert-buy__receive-amount"]}>≈ {receiveAmount}</span>
                ) : (
                  <div className={`${styles["convert-buy__range"]} ${styles["convert-buy__range--placeholder"]}`}>
                    {receiveRange}
                  </div>
                )}
              </div>
            </div>
          </div>
        }
      />

      <ConvertCurrencyDrawer
        open={currencyDrawerOpen}
        initialSide={currencyDrawerSide}
        fromCurrency={fromCurrency}
        toCurrency={toCurrency}
        fromOptions={fromOptions}
        toOptions={toOptions}
        fromBalanceMap={fromBalanceMap}
        showFromBalances={showFromBalances}
        onClose={() => setCurrencyDrawerOpen(false)}
        onFromChange={onFromChange}
        onToChange={onToChange}
      />
    </>
  );
}
